/*
  Number and money formatting, and the colour palette, shared by everything that renders.

  These are small, and that is exactly why they belong here rather than being retyped per surface: a
  deck that rounds a salary differently from the screen it was exported from is a defect the reader
  cannot see and the author cannot explain. `fmt` and `fmtMoney` are ported verbatim from
  orgchart.html:2657-2658 and `fmtMoneyCompact` from :4268, down to the sign going before the € — the
  original's comment on that line says "−€21k, not €-21k", and it is the kind of detail that gets lost
  and then noticed in a board pack.
*/
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/**
 * A month index as the reader should see it: "M6" on the index-only clock, "Mar 27" once Day 1 has
 * a date.
 *
 * The ARITHMETIC stays on the indices everywhere — this is a rendering of the same clock, not a
 * second clock — which is what keeps every engine untouched by the calendar. Parsed by hand rather
 * than through Date so an invalid stored string (which `mergeAssumptions` already screens) degrades
 * to the index label instead of "NaN NaN".
 */
export function monthLabel(m, assumptions) {
    const parsed = /^(\d{4})-(\d{2})-\d{2}$/.exec(assumptions.day1Date ?? '');
    if (!parsed)
        return `M${m}`;
    const months = Number(parsed[1]) * 12 + (Number(parsed[2]) - 1) + m;
    return `${MONTH_NAMES[months % 12]} ${String(Math.floor(months / 12)).slice(2)}`;
}
/**
 * The month index a calendar date falls in, relative to Day 1 — the inverse of `monthLabel`.
 *
 * WHY IT HAS TO EXIST AT ALL: every number in this product lives on a "months after Day 1" clock,
 * and everything that actually HAPPENS carries a date. Without this function a real departure and a
 * planned one cannot be put on the same axis, which is the whole of why plan-versus-actual has been
 * unbuildable — not for want of a comparison, for want of a shared clock.
 *
 * NULL WHEN THERE IS NO DAY 1, rather than 0. A project running on indices alone has not said when
 * Day 1 is, and answering "month 0" would date every observation to the close — the most confident
 * possible wrong answer. The caller shows the date instead.
 *
 * WHOLE MONTHS, FLOORED, on calendar months rather than 30-day blocks, so it agrees with
 * `monthLabel` exactly: a date inside the month `monthLabel(m)` names returns m. Negative for
 * anything before Day 1, which is a real and reportable answer — a departure during consultation
 * happens before the close.
 */
export function monthOfDate(iso, assumptions) {
    const day1 = /^(\d{4})-(\d{2})-\d{2}$/.exec(assumptions.day1Date ?? '');
    const at = /^(\d{4})-(\d{2})-\d{2}/.exec(iso);
    if (!day1 || !at)
        return null;
    const from = Number(day1[1]) * 12 + (Number(day1[2]) - 1);
    const to = Number(at[1]) * 12 + (Number(at[2]) - 1);
    return to - from;
}
/** Day 1 itself, readably — "3 Sep 2026" — or null while the project runs on indices alone. */
export function day1Label(assumptions) {
    const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(assumptions.day1Date ?? '');
    if (!parsed)
        return null;
    return `${Number(parsed[3])} ${MONTH_NAMES[Number(parsed[2]) - 1]} ${parsed[1]}`;
}
/** Grouped integer, or an em dash for nothing. `d` is the maximum number of decimals. */
export function fmt(n, d = 0) {
    if (n === null || n === undefined || Number.isNaN(Number(n)))
        return '—';
    return Number(n).toLocaleString('en-US', { maximumFractionDigits: d });
}
/** Whole euros, grouped. */
export function fmtMoney(n) {
    if (n === null || n === undefined || Number.isNaN(Number(n)))
        return '—';
    return `€${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}
/**
 * Compact money for tight card lines and deck boxes: €96k / €1.2m.
 *
 * Note it does not em-dash a nullish value the way the other two do — it renders €0. That asymmetry is
 * the original's (`Number(v) || 0`) and it is load-bearing on a card, where a blank cost reads as
 * missing data rather than as no cost.
 */
export function fmtMoneyCompact(v) {
    const n = Number(v) || 0;
    const a = Math.abs(n);
    const s = n < 0 ? '−' : '';
    if (a >= 1e6)
        return `${s}€${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1)}m`;
    if (a >= 1e3)
        return `${s}€${Math.round(a / 1e3)}k`;
    return `${s}€${Math.round(a)}`;
}
const PALETTE = {
    ink: '0C233C',
    muted: '64748B',
    danger: 'EF4444',
    violet: '7213EA',
    pink: 'FD349C',
    primary: '00338D',
    // The two the analytics charts need and this table did not have: the below-zero surplus band on the
    // stranded and transition charts, and the late-replacement marker on the exit timeline. Without them a
    // pane author either hard-codes `var(--c-success)` inside drawing code — the ad-hoc lookup this table
    // exists to abolish, and one the deck cannot resolve — or borrows `nodeDisplay.colors.status`, which is
    // project state edited through a command, so a card-palette change would silently repaint what green
    // MEANS on a cost chart.
    //
    // `success` is a step darker than the stylesheet's #10b981: that value measures 2.54:1 on white, below
    // the 3:1 a mark carrying meaning needs. The bucket palette stays separate — anything drawing a BUCKET
    // takes `nodeDisplay.colors.status`, so the chart and the cards cannot disagree about Carve-out.
    success: '0D8F6B',
    warn: 'F59E0B',
    /*
      The two cost steps a signed money chart stacks above its zero line.
  
      `primary` is deliberately not one of them: at 00338D it is outside the lightness band a series colour
      has to sit in, which is what makes it a good chrome colour and a bad fill. `accent` and `accent2` are
      one hue at two steps — an ordinal pair, because the two components stacked here are both costs and the
      split is part-to-whole, not identity. Two DIFFERENT hues would have been wrong twice over: it would
      claim the segments are unrelated, and the obvious second hue (violet) sits ΔE 1.3 from accent under
      deuteranopia, which is indistinguishable. accent2 clears the light surface at 3.10:1.
    */
    accent: '1E49E2',
    accent2: '6D8EF0',
};
/** Six hex digits, no leading `#` — the form PptxGenJS takes. */
export function hexOf(token) {
    return PALETTE[token];
}
/** The CSS custom property the apps' stylesheets define for the same colour. */
export function cssVarOf(token) {
    return `var(--c-${token})`;
}
/**
 * What each legal-entity classification is called, and the colour that carries it.
 *
 * Ported from `ENTITY_CLASSES` (orgchart.html:7699), which was the only place either existed. In
 * core rather than in the client because a colour that means something is a fact about the DOMAIN,
 * and the deck resolves the same tokens through `hexOf` — a classification that is violet on screen
 * and something else on a slide is the class of disagreement the palette table exists to abolish.
 *
 * The tokens are the app's own choices and are kept: `retain` reads as the default blue, `transfer`
 * and `new` are the two that mean something is moving, and `dissolve` borrows the danger hue because
 * it is the only one of the four that is an ending. None is ever the sole channel — every surface
 * drawing these ships the label beside the mark.
 */
export const ENTITY_CLASS_META = {
    retain: { label: 'Retain', color: 'primary' },
    transfer: { label: 'Transfer', color: 'violet' },
    new: { label: 'New (NewCo)', color: 'pink' },
    dissolve: { label: 'Dissolve', color: 'danger' },
};
export const METRICS = {
    hc: { label: 'Headcount', short: 'HC', format: (v) => fmt(Math.round(v)) },
    fte: { label: 'FTE', short: 'FTE', format: (v) => fmt(v, 1) },
    cost: { label: 'Cost', short: '€', format: (v) => fmtMoneyCompact(v) },
};
/**
 * Cost is formatted differently depending on where it appears, and both spellings are the original's.
 *
 * `CMP_METRICS.cost.fmt` is full `fmtMoney` — that is what the bridge slide's bar labels and the
 * analytics tables use. `STAGE_LINE_METRICS`' cost entry is `fmtMoneyCompact`, because a stage line
 * puts four stages on one line inside a 2-inch box. Keeping both named stops one being "corrected"
 * into the other.
 */
export const METRIC_FULL_FORMAT = {
    hc: (v) => fmt(Math.round(v)),
    fte: (v) => fmt(v, 1),
    cost: (v) => fmtMoney(v),
};
/** Stage-comparison line: which stages can appear, in order. Ported from :4281. */
export const STAGE_LINE_STAGES = [
    { key: 'asis', short: 'A', label: 'As-is (whole organisation)' },
    { key: 'p', short: 'P', label: 'Perimeter' },
    { key: 'd1', short: 'D1', label: 'Day 1' },
    { key: 'd2', short: 'D2', label: 'Day 2' },
    { key: 'r1', short: 'R1', label: 'RemainCo after Day 1 (existing people)' },
    { key: 'rs', short: 'RS', label: 'RemainCo steady state (after planned rightsizings)' },
];
/** Stage-comparison line: which metrics get a line each. Ported from :4283. */
export const STAGE_LINE_METRICS = [
    { key: 'hc', short: 'HC', format: (v) => fmt(v) },
    { key: 'fte', short: 'FTE', format: (v) => fmt(v, 1) },
    { key: 'cost', short: '€', format: (v) => fmtMoneyCompact(v) },
];
//# sourceMappingURL=format.js.map